import { dollarsToCents, MEDICARE_NO_LIMIT_OOP_CENTS } from "./price";

/**
 * CLE-2635 — resolving an order's eligibility benefits.
 *
 * Benefit values have one read model (`GfeQuote`'s cents columns) but two write
 * paths. The Athena CSV import skips quote creation when no procedure resolves
 * (`athena-import.service.ts` — `if (parsedProcedures.length > 0)`), while its
 * background eligibility check stores the pVerify result on the order regardless.
 * Those orders therefore carry real benefits in `benefitDetails` and no quote at
 * all, and every surface reading `gfeQuote?.remainingDeductibleInCents || 0`
 * renders $0.00.
 *
 * `benefitDetails.response` holds DOLLARS (as pVerify returns them); `GfeQuote`
 * holds CENTS. Everything below normalises to cents.
 */

export type BenefitsSource = "quote" | "eligibility" | "none";

export interface IResolvedBenefits {
  remainingDeductibleInCents: number;
  outOfPocketMaxInCents: number;
  flatCopayInCents: number;
  professionalFlatCopayInCents: number;
  coinsurancePercent: number;
  /** Null when derived from eligibility — the pVerify response carries no timestamp. */
  benefitsUpdatedAt: string | null;
  source: BenefitsSource;
}

/** The minimum shape needed. Kept structural so every order type can pass. */
export interface IOrderWithBenefits {
  gfeQuote?: any;
  benefitDetails?: any;
  secondaryBenefitDetails?: any;
}

const EMPTY: IResolvedBenefits = {
  remainingDeductibleInCents: 0,
  outOfPocketMaxInCents: 0,
  flatCopayInCents: 0,
  professionalFlatCopayInCents: 0,
  coinsurancePercent: 0,
  benefitsUpdatedAt: null,
  source: "none",
};

/**
 * A usable eligibility payload. Rejects both the null archive and the
 * error-shaped response (`{ error: "..." }`) that a failed pVerify check leaves
 * behind — 26 such orders exist in production.
 */
const hasEligibilityBenefits = (details: any): boolean =>
  !!details?.response &&
  typeof details.response === "object" &&
  "remainingDeductible" in details.response;

/**
 * pVerify signals "no out-of-pocket maximum" with a boolean; pricing encodes it
 * as a sentinel amount. Translate rather than passing a literal 0, which would
 * read as "fully met".
 */
const oopMaxToCents = (response: any): number =>
  response?.isRemainingOutOfPocketMaxInfinity
    ? MEDICARE_NO_LIMIT_OOP_CENTS
    : dollarsToCents(response?.remainingOutOfPocketMax || 0);

/** Maps a pVerify response (dollars) onto the quote's cents-based shape. */
const benefitsFromEligibility = (details: any): IResolvedBenefits => {
  const { response } = details;

  return {
    remainingDeductibleInCents: dollarsToCents(
      response.remainingDeductible || 0
    ),
    outOfPocketMaxInCents: oopMaxToCents(response),
    flatCopayInCents: dollarsToCents(response.flatCopay || 0),
    professionalFlatCopayInCents: dollarsToCents(
      response.professionalCopay || 0
    ),
    coinsurancePercent: response.coinsurancePercent || 0,
    benefitsUpdatedAt: null,
    source: "eligibility",
  };
};

/**
 * Primary benefits, in cents.
 *
 * Precedence is quote-first and unconditional: when a quote exists it IS the
 * answer, including when its values are zero. A deliberately zeroed deductible
 * must not be overwritten by a stale eligibility archive — we only derive when
 * there is no quote to read.
 */
export const resolveOrderBenefits = (
  order: IOrderWithBenefits | null | undefined
): IResolvedBenefits => {
  const quote = order?.gfeQuote;

  if (quote) {
    return {
      remainingDeductibleInCents: quote.remainingDeductibleInCents || 0,
      outOfPocketMaxInCents: quote.outOfPocketMaxInCents || 0,
      flatCopayInCents: quote.flatCopayInCents || 0,
      professionalFlatCopayInCents: quote.professionalFlatCopayInCents || 0,
      coinsurancePercent: quote.coinsurancePercent || 0,
      benefitsUpdatedAt: quote.benefitsUpdatedAt || null,
      source: "quote",
    };
  }

  if (!hasEligibilityBenefits(order?.benefitDetails)) {
    return EMPTY;
  }

  return benefitsFromEligibility(order?.benefitDetails);
};

/**
 * CLE-3050 — the clinic imaging copay, in cents (primary only), or null when the
 * order has none. Same precedence as `resolveOrderBenefits`: the quote when one
 * exists (NULL there means none), otherwise the eligibility response's
 * `imagingCopay` (dollars). Kept apart from `IResolvedBenefits` so every
 * surface that reads those five fields is unchanged.
 */
export const resolveOrderImagingCopayInCents = (
  order: IOrderWithBenefits | null | undefined
): number | null => {
  const quote = order?.gfeQuote;
  if (quote) {
    return typeof quote.imagingCopayInCents === "number"
      ? quote.imagingCopayInCents
      : null;
  }
  const imagingCopay = order?.benefitDetails?.response?.imagingCopay;
  return typeof imagingCopay === "number" ? dollarsToCents(imagingCopay) : null;
};

/**
 * Secondary benefits, in cents.
 *
 * The quote stores these as a JSON blob under different key names than the
 * primary columns (`deductible`, not `remainingDeductibleInCents`).
 */
export const resolveOrderSecondaryBenefits = (
  order: IOrderWithBenefits | null | undefined
): IResolvedBenefits => {
  const secondary = order?.gfeQuote?.secondaryPatientBenefits;

  if (secondary) {
    return {
      remainingDeductibleInCents: secondary.deductible || 0,
      outOfPocketMaxInCents: secondary.outOfPocketMax || 0,
      flatCopayInCents: secondary.flatCopay || 0,
      professionalFlatCopayInCents: secondary.professionalFlatCopay || 0,
      coinsurancePercent: secondary.coinsurancePercent || 0,
      benefitsUpdatedAt: secondary.benefitsUpdatedAt || null,
      source: "quote",
    };
  }

  if (!hasEligibilityBenefits(order?.secondaryBenefitDetails)) {
    return EMPTY;
  }

  return benefitsFromEligibility(order?.secondaryBenefitDetails);
};

/**
 * Whether a secondary benefits block should render at all. Surfaces previously
 * gated on `gfeQuote.secondaryPatientBenefits`, which Athena orders never have.
 */
export const hasSecondaryBenefits = (
  order: IOrderWithBenefits | null | undefined
): boolean => resolveOrderSecondaryBenefits(order).source !== "none";

/**
 * CLE-2866 — whether one side's physician copay replaces the physician-side
 * deductible and co-insurance (an FEP BASIC plan; the API decides and says so
 * with `professionalCopayOnly`). Needs a physician copay still on the form, so
 * clearing it by hand gives the deductible and co-insurance back.
 *
 * Read by the benefit sections' reset effects, which re-tick every rule box
 * whenever the facility copay changes — and an eligibility run that sets the
 * physician copay usually changes the facility copay in the same breath.
 */
export const isPhysicianCopayOnly = (
  benefitDetails:
    | { response?: { professionalCopayOnly?: boolean } }
    | null
    | undefined,
  professionalFlatCopay: number | null | undefined
): boolean =>
  Boolean(benefitDetails?.response?.professionalCopayOnly) &&
  Number(professionalFlatCopay ?? 0) > 0;
